import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import mixpanel from "mixpanel-browser";
import {
  subscriptionsSelector,
  subscribeProbillStatus,
  unsubscribeProbillStatus,
  subscribeProbillStatusEmployee,
  unsubscribeProbillStatusEmployee
} from "../../../slices/subscriptions/subscriptionsSlice";
import SubscriptionsState from "../../../slices/subscriptions/SubscriptionsState";
import ConfirmationModal from "../../../ui-components/confirmation-modal/confirmationModal";
import Loading from "../../../ui-components/loading/loading";
import XGSIcon from "../../../ui-components/icon/xgsIcon";
import XGSIcons from "../../../ui-components/icon/xgsIcons";
import { userSelector } from "../../../slices/user/userSlice";
import UserState from "../../../slices/user/UserState";
import { UserUtils } from "../../../app/data/user/userUtils";
import EmployeeNotificationService from "../../../app/data/notifications/employeeNotificationService";

export interface ShipmentStatusSubscribeProps {
  invoiceNumber: number;
  subscribed: boolean;
  userTimezone: string | null;
}

const ShipmentStatusSubscribe: React.FC<ShipmentStatusSubscribeProps> = (props) => {
  const dispatch = useDispatch();
  const subscriptionsState: SubscriptionsState = useSelector(subscriptionsSelector);
  const userState: UserState = useSelector(userSelector);
  const [showSubscribeConfirmation, setShowSubscribeConfirmation] = useState<boolean>(false);
  const [empSubscribed, setEmpSubscribed] = useState<boolean | undefined>(undefined);

  React.useEffect(() => {
    const load = async () => {
      if (!UserUtils.isEmployee(userState.profile)) return;
      const email = userState.profile?.email;
      if (!email) return;
      const response = await EmployeeNotificationService.getInstance().getSubscribedProbills(email);
      if ((response as any).ok && (response as any).ok()) {
        const list = (response as any).data as number[];
        setEmpSubscribed(!!list?.includes(props.invoiceNumber));
      } else {
        setEmpSubscribed(false);
      }
    };
    load();
    // eslint-disable-next-line
  }, [userState.profile?.email, props.invoiceNumber]);

  const subscribe = () => {
    const timezone = props.userTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (UserUtils.isEmployee(userState.profile)) {
      const email = userState.profile?.email || "";
      dispatch(subscribeProbillStatusEmployee(email, props.invoiceNumber, () => {
        setShowSubscribeConfirmation(false);
        toast.info("You subscribed to status updates", { autoClose: 3000 });
        mixpanel.track("Subscribed to probill status updates", {
          "Probill": props.invoiceNumber
        });
        setEmpSubscribed(true);
      }));
      return;
    } else {
      dispatch(subscribeProbillStatus(props.invoiceNumber, timezone, () => {
        setShowSubscribeConfirmation(false);
        toast.info("You subscribed to status updates", { autoClose: 3000 });
        mixpanel.track("Subscribed to probill status updates", {
          "Probill": props.invoiceNumber
        });
      }));
    }
  };

  const unsubscribe = () => {
    if (UserUtils.isEmployee(userState.profile)) {
      const email = userState.profile?.email || "";
      dispatch(unsubscribeProbillStatusEmployee(email, props.invoiceNumber, () => {
        toast.info("You unsubscribed from status updates", { autoClose: 3000 });
        mixpanel.track("Unsubscribed from probill status updates", {
          "Probill": props.invoiceNumber
        });
        setEmpSubscribed(false);
      }));
      return;
    } else {
      dispatch(unsubscribeProbillStatus(props.invoiceNumber, () => {
        toast.info("You unsubscribed from status updates", { autoClose: 3000 });
        mixpanel.track("Unsubscribed from probill status updates", {
          "Probill": props.invoiceNumber
        });
      }));
    }
  };

  const isSubscribed = UserUtils.isEmployee(userState.profile) ? !!empSubscribed : props.subscribed;

  return (
    <>
      <span className="xgs-shipment-details__subscribe__button">
        {!subscriptionsState.subscribeStarted && (
          <>
            {!isSubscribed && (
              <div
                className="xgs-shipment-details__subscribe__button xgs-site__content-container__toolbar__buttons__item"
                onClick={() => setShowSubscribeConfirmation(true)}
              >
                <XGSIcon
                  className="xgs-shipment-details__subscribe__button__icon"
                  icon={XGSIcons.faBell}
                  size="lg"
                  title="Subscribe to status updates"
                />
                <div className="xgs-shipment-details__subscribe__button__label">Subscribe</div>
              </div>
            )}
            {isSubscribed && (
              <div
                className="xgs-shipment-details__subscribe__button xgs-shipment-details__subscribe__button--unsubscribe xgs-site__content-container__toolbar__buttons__item"
                onClick={unsubscribe}
              >
                <XGSIcon
                  className="xgs-shipment-details__subscribe__button__icon"
                  icon={XGSIcons.faBellSlash}
                  size="lg"
                  title="Unsubscribe from status updates"                  
                />
                <div className="xgs-shipment-details__subscribe__button__label">Unsubscribe</div>
              </div>
            )}
          </>
        )}
        {(subscriptionsState.subscribeStarted) && (
          <div style={{ marginTop: 8, marginRight: 12 }}>
            <Loading isLoading={true} />
          </div>
        )}
      </span>
      <ConfirmationModal
        opened={showSubscribeConfirmation}
        header="Subscribe to status updates"
        confirmButtonText="Subscribe"
        spinner={subscriptionsState.subscribeStarted}
        onCancel={() => setShowSubscribeConfirmation(false)}
        onConfirm={subscribe}
      >        
        {UserUtils.isEmployee(userState.profile) ? (
          <div className="xgs-shipment-details__subscribe__confirmation__text">
            You are about to subscribe to shipment status updates. All status updates for this probill will be shown on OneXGS.
          </div>
        ) : (
          <div className="xgs-shipment-details__subscribe__confirmation__text">
            You are about to subscribe to shipment status updates. You can then unsubscribe from these notifications on the shipment page or on the <Link className="blue-link" to="/settings/subscriptions" target="_blank">dedicated subscriptions page</Link>.
          </div>
        )}
      </ConfirmationModal>
    </>
  );
};

export default ShipmentStatusSubscribe;
